/* eslint-disable max-lines */
import { lazy } from 'react';

import { ALL_ROLE_VALUES, ROLES } from './users';

const Home = lazy(() => import('#screens/Home'));
const Login = lazy(() => import('#screens/Login'));
const LoginRedirect = lazy(() => import('#screens/LoginRedirect'));
const LawyerOnboarding = lazy(() => import('#screens/LawyerOnboarding'));
const File = lazy(() => import('#screens/File'));
const Notifications = lazy(() => import('#screens/Notifications'));
const Schedule = lazy(() => import('#screens/Schedule'));
const Profile = lazy(() => import('#screens/Profile'));
const PDF = lazy(() => import('#screens/PDF'));
const Credentials = lazy(() => import('#screens/Credentials'));
const Checkout = lazy(() => import('#screens/Checkout'));
const SubscriptionBuilder = lazy(() => import('#screens/SubscriptionBuilder'));
const SubscriptionCancellation = lazy(() => import('#screens/SubscriptionCancellation'));
const ErrorScreen = lazy(() => import('#screens/ErrorScreen'));
const WorkspaceCreationRedirect = lazy(() => import('#screens/WorkspaceCreationRedirect'));
const WorkspaceInvitationRedirect = lazy(() => import('#screens/WorkspaceInvitationRedirect'));
const Workspace = lazy(() => import('#screens/Workspace'));
const WorkspaceConfiguration = lazy(() => import('#screens/WorkspaceConfiguration'));
const ElectronicCards = lazy(() => import('#screens/ElectronicCards'));
const Projects = lazy(() => import('#screens/Projects'));
const ProjectDetail = lazy(() => import('#screens/ProjectDetail'));
const Forms = lazy(() => import('#screens/Forms'));
const FormDetail = lazy(() => import('#screens/FormDetail'));
const Templates = lazy(() => import('#screens/Templates'));
const TemplateDetail = lazy(() => import('#screens/TemplateDetail'));
const LegalCases = lazy(() => import('#screens/LegalCases'));
const LegalCaseEjeDetail = lazy(() => import('#screens/LegalCaseEjeDetail'));
const LegalCaseMevDetail = lazy(() => import('#screens/LegalCaseMevDetail'));
const LegalCaseManualDetail = lazy(() => import('#screens/LegalCaseManualDetail'));
const PlatformAdmin = lazy(() => import('#screens/PlatformAdmin'));

export const ROUTES = {
  HOME: {
    path: '/files',
    hasPremiumGate: true,
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: Home,
    withOverflow: true
  },
  PDF: {
    path: '/pdf/:pdfName',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: PDF,
    withOverflow: true,
    noMenu: true,
    noTopBanner: true
  },
  CHECKOUT: {
    path: '/checkout',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: [ROLES.OWNER],
    publicRoute: false,
    component: Checkout,
    withOverflow: true,
    noMenu: true,
    noTopBanner: true
  },
  // Legacy path (emails, old links): renders the subscription builder
  SUBSCRIPTION_PLANS: {
    path: '/planes',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: SubscriptionBuilder,
    withOverflow: true,
    noTopBanner: true
  },
  LOGIN_REDIRECT: {
    path: '/login_redirect',
    basePath: '',
    publicRoute: true,
    supportedRoles: ALL_ROLE_VALUES,
    exact: false,
    component: LoginRedirect,
    noMenu: true,
    noTopBanner: true
  },
  LOGIN: {
    path: '/login',
    basePath: '',
    publicRoute: true,
    supportedRoles: ALL_ROLE_VALUES,
    exact: false,
    component: Login,
    noMenu: true,
    noTopBanner: true
  },
  LAWYER_ONBOARDING: {
    path: '/onboarding/lawyer',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: LawyerOnboarding,
    needsOnboarding: false,
    noMenu: true,
    noTopBanner: true
  },
  FILE_DETAILS: {
    path: '/files/:id',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: File,
    withOverflow: true
  },
  NOTIFICATIONS: {
    path: '/notifications',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: Notifications,
    withOverflow: true
  },
  SCHEDULE: {
    path: '/agenda',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: Schedule,
    withOverflow: true
  },
  PROFILE: {
    path: '/profile',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: Profile,
    withOverflow: true
  },
  CREDENTIALS: {
    path: '/credentials',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: Credentials,
    withOverflow: true
  },
  SUBSCRIPTION_CANCELLATION: {
    path: '/subscription/cancel',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: [ROLES.OWNER],
    publicRoute: false,
    component: SubscriptionCancellation,
    withOverflow: true,
    noMenu: true,
    noTopBanner: true
  },
  WORKSPACE_CREATION_REDIRECT: {
    path: '/workspace_creation_redirect',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: [ROLES.OWNER, ROLES.LAWYER],
    publicRoute: false,
    component: WorkspaceCreationRedirect,
    noMenu: true,
    noTopBanner: true
  },
  ERROR: {
    path: '/error',
    basePath: '',
    exact: true,
    publicRoute: true,
    privateRoute: true,
    component: ErrorScreen,
    withOverflow: true,
    noMenu: true,
    noTopBanner: true
  },
  WORKSPACE_INVITATION_REDIRECT: {
    path: '/workspace_invitation_redirect',
    basePath: '',
    publicRoute: true,
    supportedRoles: ALL_ROLE_VALUES,
    exact: false,
    component: WorkspaceInvitationRedirect,
    noMenu: true,
    noTopBanner: true,
    noRedirect: true
  },
  WORKSPACE: {
    path: '/workspace',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: Workspace,
    withOverflow: true
  },
  WORKSPACE_CONFIGURATION: {
    path: '/workspace/configuration',
    availableWithoutAccess: true,
    basePath: '',
    exact: true,
    supportedRoles: [ROLES.OWNER, ROLES.ADMIN],
    component: WorkspaceConfiguration,
    withOverflow: true
  },
  PLATFORM_ADMIN: {
    path: '/admin/metrics',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: PlatformAdmin,
    withOverflow: true
  },
  ELECTRONIC_CARDS: {
    path: '/electronic_cards',
    hasPremiumGate: true,
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: ElectronicCards,
    withOverflow: true
  },
  PROJECTS: {
    path: '/proyectos',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: Projects,
    withOverflow: true
  },
  PROJECT_DETAIL: {
    path: '/proyectos/:id',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: ProjectDetail,
    withOverflow: true
  },
  FORMS: {
    path: '/formularios',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: Forms,
    withOverflow: true
  },
  FORM_DETAIL: {
    path: '/formularios/:id',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: FormDetail,
    withOverflow: true
  },
  TEMPLATES: {
    path: '/templates',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: Templates,
    withOverflow: true
  },
  TEMPLATE_DETAIL: {
    path: '/templates/:templateId',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: TemplateDetail,
    withOverflow: true
  },
  LEGAL_CASES: {
    path: '/expedientes',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    component: LegalCases,
    withOverflow: true
  },
  LEGAL_CASE_EJE_DETAIL: {
    path: '/expedientes/eje/:id',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: LegalCaseEjeDetail,
    withOverflow: true
  },
  LEGAL_CASE_MEV_DETAIL: {
    path: '/expedientes/mev/:id',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: LegalCaseMevDetail,
    withOverflow: true
  },
  LEGAL_CASE_MANUAL_DETAIL: {
    path: '/expedientes/manual/:id',
    basePath: '',
    exact: true,
    supportedRoles: ALL_ROLE_VALUES,
    publicRoute: false,
    component: LegalCaseManualDetail,
    withOverflow: true
  }
};

export const DEFAULT_ROUTE_PATH = {
  [ROLES.LAWYER]: ROUTES.LEGAL_CASES.path,
  [ROLES.ADMIN]: ROUTES.LEGAL_CASES.path,
  [ROLES.OWNER]: ROUTES.LEGAL_CASES.path
};
